import { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import Icon from '../components/Icon.jsx';
import { Mail, MessageSquare, Phone, Send } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSendContactMessageMutation } from '../slices/newsApiSlice';

const SUPPORT_EMAIL = 'ethan@leaguerunner.net';

const ContactScreen = () => {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [sendContactMessage, { isLoading }] = useSendContactMessageMutation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await sendContactMessage(formData).unwrap();
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
      toast.success('Message sent to League Runner support.');
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to send message.');
      setSubmitted(false);
    }
  };

  return (
    <div className="section-modern">
      <Container className="fade-in py-5">
        <h1 className="text-primary mb-4">
          <Icon icon={Mail} size={24} className="me-2 align-middle" /> Contact League Runner
        </h1>
        <p className="text-secondary lead mb-4">
          Have some feedback for the site? Ethan would love to hear from you. If you have questions,
          ideas, or just want to share your experience with League Runner, reach out directly at{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
        <Row className="g-4">
          <Col md={4}>
            <Card className="modern-card h-100">
              <Card.Body className="text-center">
                <Icon icon={MessageSquare} size={28} className="mb-3" />
                <h5 className="card-title">Feedback</h5>
                <p className="text-secondary mb-0 small">
                  Share ideas, suggestions, and thoughts on how League Runner can improve.
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="modern-card h-100">
              <Card.Body className="text-center">
                <Icon icon={Phone} size={28} className="mb-3" />
                <h5 className="card-title">Contact</h5>
                <p className="text-secondary mb-0 small">
                  Reach Ethan Pelton directly for questions about the site or your experience using it.
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="modern-card h-100">
              <Card.Body className="text-center">
                <Icon icon={Send} size={28} className="mb-3" />
                <h5 className="card-title">Email</h5>
                <p className="text-secondary mb-0 small">
                  <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="modern-card mt-5">
          <Card.Body>
            <h3 className="modern-card-title mb-3">Send Ethan a Message</h3>
            {submitted && (
              <Alert variant="success" className="py-2 mb-4">
                Your message was sent to League Runner support.
              </Alert>
            )}
            <Form onSubmit={handleSubmit} className="contact-form-modern">
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="contactName">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="contactEmail">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="contactMessage">
                    <Form.Label>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="How can we help?"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Button type="submit" variant="primary" className="btn-primary-modern" disabled={isLoading}>
                    {isLoading ? 'Sending...' : 'Submit'}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default ContactScreen;
